import moment from "moment-timezone";
import { i18n } from "@/i18n";

// Shared by Settings › การจัดส่ง (Facebook Pages card), the order page timeline and the orders table column.

const TZ = "Asia/Bangkok";
const TH_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

const t = (key, params) => i18n.t("messenger.tracking." + key, params);

// 2 ส.ค. 2569 (Bangkok day, Buddhist-era year); "" when absent
export const thDate = (ms) => {
  if (ms == null) return "";
  const m = moment(ms).tz(TZ);
  return `${m.date()} ${TH_MONTHS[m.month()]} ${m.year() + 543}`;
};

// 15 ต.ค. 2569 22:04 น.
export const thDateTime = (ms) => (ms == null ? "" : `${thDate(ms)} ${moment(ms).tz(TZ).format("HH:mm")} น.`);

// The seller writes Thai tokens; the API stores named ones. Old rows may still hold Meta's
// positional {{1}}, and older custom wordings the previous Thai tracking token: both are
// accepted on load and mapped to the current display token.
export const TOKENS = {
  name: { api: "{{name}}", display: "{{ชื่อลูกค้า}}" },
  tracking: { api: "{{tracking}}", display: "{{เลขพัสดุ}}" },
  cod: { api: "{{cod}}", display: "{{ยอดเก็บเงินปลายทาง}}" },
};
export const TRACKING_TOKEN = TOKENS.tracking.display;
export const COD_TOKEN = TOKENS.cod.display;
export const NAME_TOKEN = TOKENS.name.display;
const LEGACY_TRACKING = "{{1}}";
const LEGACY_TRACKING_TH = "{{เลขแทร็ค}}";
export const toDisplayBody = (body) =>
  String(body || "")
    .split(LEGACY_TRACKING).join(TOKENS.tracking.display)
    .split(LEGACY_TRACKING_TH).join(TOKENS.tracking.display)
    .split(TOKENS.tracking.api).join(TOKENS.tracking.display)
    .split(TOKENS.cod.api).join(TOKENS.cod.display)
    .split(TOKENS.name.api).join(TOKENS.name.display);
export const toApiBody = (text) =>
  String(text || "")
    .split(LEGACY_TRACKING_TH).join(TOKENS.tracking.api)
    .split(TOKENS.tracking.display).join(TOKENS.tracking.api)
    .split(TOKENS.cod.display).join(TOKENS.cod.api)
    .split(TOKENS.name.display).join(TOKENS.name.api);

// Sample values and limits for the tracking-message previews and editor (as in the mockup)
export const SAMPLE_TRACKING_NO = "TH0123456789";
export const SAMPLE_COD = "1,290";
export const SAMPLE_NAME = "สมชาย";
export const MAX_TEMPLATE_BODY = 2000;
export const TEMPLATE_COUNT_FROM = 1800;

export const countToken = (body, token) => String(body || "").split(token).length - 1;

// The standard COD wording (backend DEFAULT_TEMPLATES.shipped_cod.body) in the seller's tokens.
// The editor seeds with this when the Page has no custom copy, and the COD preview of the
// standard copy uses it. Never seeded from defaultBody (that is the non-COD wording).
export const DEFAULT_COD_BODY = toDisplayBody(
  "สวัสดี {{name}} คำสั่งซื้อของคุณได้รับการจัดส่งแล้ว!\n\nหมายเลขติดตามของคุณคือ {{tracking}}\nเก็บเงินปลายทาง {{cod}} บาท\n\nกดปุ่มด้านล่างเพื่อติดตามการจัดส่ง"
);

// Backend plainVariantBody on the API-token body: every line holding {{cod}} dropped
export const plainVariantBody = (body) =>
  String(body)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((line) => line.indexOf(TOKENS.cod.api) === -1)
    .map((line) => (line.trim() === "" ? "" : line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const trimStartStr = (s) => s.replace(/^\s+/, "");
const trimEndStr = (s) => s.replace(/\s+$/, "");
const touchesEdge = (text, token) => trimStartStr(text).indexOf(token) === 0 || trimEndStr(text).slice(-token.length) === token;

// Port of the backend validateCustomBody (+ plain variant check) on the API-token body.
// Returns null when acceptable, else the rule: empty | too_long | unknown_token | missing | name_once | sameline | edge
export const bodyProblem = (apiBody) => {
  const body = String(apiBody == null ? "" : apiBody);
  const t = TOKENS;
  if (!body.trim()) return "empty";
  if (body.length > MAX_TEMPLATE_BODY) return "too_long";
  const found = body.match(/\{\{[^}]*\}\}/g) || [];
  if (found.some((tok) => tok !== t.name.api && tok !== t.tracking.api && tok !== t.cod.api)) return "unknown_token";
  const count = (tok) => found.filter((f) => f === tok).length;
  if (count(t.tracking.api) !== 1 || count(t.cod.api) !== 1) return "missing";
  if (count(t.name.api) > 1) return "name_once";
  const codLine = body.split("\n").find((line) => line.indexOf(t.cod.api) !== -1);
  if (codLine && codLine.indexOf(t.tracking.api) !== -1) return "sameline";
  if ([t.tracking.api, t.cod.api, t.name.api].some((tok) => touchesEdge(body, tok))) return "edge";
  const plain = plainVariantBody(body);
  if ([t.tracking.api, t.name.api].some((tok) => touchesEdge(plain, tok))) return "edge";
  return null;
};

// Paid-in-full preview: drop the line holding the COD placeholder. A line holding both
// placeholders keeps the line with only the COD part removed (mockup tplDropCodLine).
export const dropCodLine = (body) =>
  String(body || "")
    .split("\n")
    .map((line) => {
      if (line.indexOf(COD_TOKEN) === -1) return line;
      if (line.indexOf(TRACKING_TOKEN) !== -1) return line.split(COD_TOKEN).join("");
      return null;
    })
    .filter((line) => line !== null)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n");

export const fillPreview = (body, track, cod, name) =>
  String(body || "").split(TRACKING_TOKEN).join(track).split(COD_TOKEN).join(cod).split(NAME_TOKEN).join(name);

export const escapeHtml = (value) =>
  String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Highlighted HTML for the editor mirror behind the transparent textarea
export const mirrorHtml = (body) =>
  escapeHtml(body)
    .split(TRACKING_TOKEN).join('<span class="ph --track">' + TRACKING_TOKEN + "</span>")
    .split(COD_TOKEN).join('<span class="ph --cod">' + COD_TOKEN + "</span>")
    .split(NAME_TOKEN).join('<span class="ph --name">' + NAME_TOKEN + "</span>") + "&#8203;";

// Preview bubble HTML with the filled sample values highlighted; dropCod renders the paid variant
export const previewHtml = (body, dropCod) => {
  const filled = fillPreview(dropCod ? dropCodLine(body) : body, SAMPLE_TRACKING_NO, SAMPLE_COD, SAMPLE_NAME);
  return escapeHtml(filled)
    .split(SAMPLE_TRACKING_NO).join('<span class="ph --track">' + SAMPLE_TRACKING_NO + "</span>")
    .split(SAMPLE_COD).join('<span class="ph --cod">' + SAMPLE_COD + "</span>")
    .split(SAMPLE_NAME).join('<span class="ph --name">' + SAMPLE_NAME + "</span>");
};

// PENDING | REJECTED | APPROVED | null from a tracking-template payload (shipped + custom shapes)
// REJECTED wins over PENDING: a saved pair (plain + COD) is refused when either half is.
export const templateReviewStatus = (template) => {
  if (!template) return null;
  const shipped = template.shipped || {};
  const custom = template.custom || {};
  const statuses = [shipped.status, custom.plainStatus, custom.codStatus];
  if (statuses.indexOf("REJECTED") !== -1) return "REJECTED";
  if (custom.queued === true || statuses.indexOf("PENDING") !== -1) return "PENDING";
  if (statuses.indexOf("APPROVED") !== -1) return "APPROVED";
  return null;
};

export const templateRejectReason = (template) => {
  if (!template) return "";
  if (template.custom && template.custom.reason) return template.custom.reason;
  if (template.shipped && template.shipped.reason) return template.shipped.reason;
  return "";
};

// Why a send failed, in the seller's words. Graph error codes first; anything unmapped returns
// null. Callers show the generic "failed" string; the raw errorMessage is for a title attribute only.
export const failureDetail = (send) => (send && send.errorMessage) || "";

export const failureReason = (send) => {
  const code = Number(send && send.errorCode);
  const message = failureDetail(send);
  if (code === 551) return t("reason_blocked");
  if (code === 190) return t("reason_token");
  if ([10, 200, 230].includes(code)) return t("reason_permission");
  if ([4, 17, 32, 613].includes(code)) return t("reason_rate");
  if (/^template /i.test(message)) return t("reason_template");
  return null;
};

// Orders table badge for the latest send of an order; null renders "–".
export const sendBadge = (send) => {
  if (!send) return null;
  const what = send.event === "cancelled" ? t("kind_cancel") : t("kind_tracking");
  if (send.status === "failed") {
    return { variant: "danger", icon: "fas fa-exclamation-circle", label: t("badge_failed"), title: [what, failureReason(send) || t("reason_generic"), failureDetail(send)].filter(Boolean).join(" · ") };
  }
  if (send.status === "sent") {
    if (send.readAt) {
      return { variant: "success", icon: "fas fa-check-double", label: t("badge_read"), title: `${what} · ${t("read")} ${thDateTime(send.readAt)}` };
    }
    const when = send.deliveredAt ? `${t("delivered")} ${thDateTime(send.deliveredAt)}` : `${t("sent_at")} ${thDateTime(send.sentAt)}`;
    return { variant: "primary", icon: "fas fa-check", label: t("badge_sent"), title: `${what} · ${when}` };
  }
  if (send.status === "sending") {
    return { variant: "order-draft", icon: "fal fa-hourglass-half", label: t("badge_pending"), title: what };
  }
  return null;
};
